/**
 * Pure rules for the Activity page, the "Since you last looked" digest and the
 * Audit trail (DASHBOARD.md §1.15, §1.18). Dates are ICT (Asia/Bangkok), the
 * same clock the bots and the rest of Grokbot use.
 */

import { ActivityEvent, ActivityType, AuditRow, Bot, Company, Snapshot } from './types';

export const ACTIVITY_TYPES: ActivityType[] = ['recommendation', 'document', 'status', 'verdict', 'reply'];
export const TYPE_LABEL: Record<ActivityType, string> = {
  recommendation: 'Recommendation', document: 'Document', status: 'Status', verdict: 'C-suite verdict', reply: 'Bot reply',
};

export type Scope = 'current' | 'both' | 'lifescribe' | 'quill';
export const SCOPES: Scope[] = ['current', 'both', 'lifescribe', 'quill'];

export interface TimelineFilters {
  scope: Scope;
  /** The company the page is showing, for scope "current". */
  company: Company;
  exec: string | null;
  type: ActivityType | 'all';
  /** ISO time: only events at or after it ("since last look"). */
  since: string | null;
  q: string;
}

export function filterTimeline(feed: ActivityEvent[], snapshot: Snapshot, f: TimelineFilters): ActivityEvent[] {
  const company: Company | null = f.scope === 'both' ? null : f.scope === 'current' ? f.company : f.scope;
  const execSlugs = f.exec && snapshot.exec_categories && snapshot.exec_categories[f.exec] ? new Set(snapshot.exec_categories[f.exec].slugs) : null;
  const since = f.since ? new Date(f.since).getTime() : null;
  const q = f.q.trim().toLowerCase();
  return feed.filter((e) => {
    if (company && e.company !== company) return false;
    if (execSlugs && !execSlugs.has(e.bot_slug)) return false;
    if (f.type !== 'all' && e.type !== f.type) return false;
    if (since != null && new Date(e.at).getTime() < since) return false;
    if (q && !`${e.title} ${e.bot_slug} ${e.path || ''}`.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** "2026-10-06" in ICT for an instant. */
export function ictDay(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Unknown date';
  return new Date(t + 7 * 3600 * 1000).toISOString().slice(0, 10);
}

export function groupByDay(events: ActivityEvent[]): { day: string; events: ActivityEvent[] }[] {
  const out: { day: string; events: ActivityEvent[] }[] = [];
  const sorted = events.slice().sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  sorted.forEach((e) => {
    const day = ictDay(e.at);
    const last = out[out.length - 1];
    if (last && last.day === day) last.events.push(e);
    else out.push({ day, events: [e] });
  });
  return out;
}

const BASE = '/dashboard/bot-ops';
const bot = (slug: string, q: string) => `${BASE}/bots/${encodeURIComponent(slug)}${q}`;

/** Where a timeline row goes: recommendation and verdict to the Recommendations tab, document to Documents, reply to the Inbox, status to Current work. */
export function eventLink(e: ActivityEvent): string {
  switch (e.type) {
    case 'recommendation':
    case 'verdict':
      return bot(e.bot_slug, `?tab=recommendations${e.rec_id ? `&rec=${encodeURIComponent(e.rec_id)}` : ''}`);
    case 'document': return bot(e.bot_slug, '?tab=documents');
    case 'reply': return bot(e.bot_slug, `?tab=inbox${e.instruction_id ? `&instruction=${encodeURIComponent(e.instruction_id)}` : ''}`);
    case 'status': return bot(e.bot_slug, '?tab=work');
    default: return bot(e.bot_slug, '');
  }
}

// ---- Team heartbeat ------------------------------------------------------------------

export type HeartbeatWindow = '48h' | '7d';

export interface HeartbeatRow {
  bot: Bot;
  /** One entry per hour, oldest first; null = no data for that day. */
  cells: (number | null)[];
  lastWorkAt: string | null;
  idleFor: string | null;
  worksEveryHours: number | null;
  jdCadence: string | null;
  alert: string | null;
}

/** Most recently active first. 48 h is the last two days of the 7-day grid; days before the team's first recorded event stay null. */
export function teamHeartbeat(snapshot: Snapshot, scope: Scope, company: Company, window: HeartbeatWindow): HeartbeatRow[] {
  const target: Company | null = scope === 'both' ? null : scope === 'current' ? company : scope;
  const rows = snapshot.bots
    .filter((b) => (target ? b.company === target : true) && b.activity)
    .map((b): HeartbeatRow => {
      const a = b.activity as NonNullable<Bot['activity']>;
      const days = (a.events_by_hour_7d || []).slice(window === '48h' ? -2 : 0);
      const cells = days.flatMap((d) => (d.hours ? d.hours : Array.from({ length: 24 }, () => null)));
      return {
        bot: b,
        cells,
        lastWorkAt: a.last_work_at || null,
        idleFor: a.idle_for || null,
        worksEveryHours: a.cadence_hours ?? null,
        jdCadence: a.jd_cadence ? a.jd_cadence.keyword : null,
        alert: a.idle_alert || null,
      };
    });
  return rows.sort((x, y) => {
    const a = x.lastWorkAt ? new Date(x.lastWorkAt).getTime() : -Infinity;
    const b = y.lastWorkAt ? new Date(y.lastWorkAt).getTime() : -Infinity;
    return b - a || (x.bot.name < y.bot.name ? -1 : 1);
  });
}

// ---- "Since you last looked" ------------------------------------------------------------

export interface Digest {
  since: string;
  /** True when there is no stored last look: the window is the 24 h before the snapshot, and the page says so. */
  firstVisit: boolean;
  newRecommendations: number;
  verdictChanges: number;
  botReplies: number;
  documentsUpdated: number;
  implementationsDone: number;
  idleAlerts: number;
}

export function digest(snapshot: Snapshot, feed: ActivityEvent[], lastSeenAt: string | null, company: Company | null): Digest {
  const firstVisit = !lastSeenAt;
  const since = lastSeenAt || new Date(new Date(snapshot.generated_ict).getTime() - 24 * 3600 * 1000).toISOString();
  const t0 = new Date(since).getTime();
  const events = feed.filter((e) => new Date(e.at).getTime() >= t0 && (!company || e.company === company));
  const count = (type: ActivityType) => events.filter((e) => e.type === type).length;
  const bots = snapshot.bots.filter((b) => !company || b.company === company);
  const implementationsDone = bots.reduce((n, b) => n + (b.actions || []).filter((a) => a.implementation && a.implementation.status === 'done' && a.implementation.updated_at && new Date(a.implementation.updated_at).getTime() >= t0).length, 0);
  return {
    since, firstVisit,
    newRecommendations: count('recommendation'), verdictChanges: count('verdict'), botReplies: count('reply'), documentsUpdated: count('document'),
    implementationsDone,
    idleAlerts: bots.filter((b) => b.activity && b.activity.idle_alert).length,
  };
}

export const digestTotal = (d: Digest): number => d.newRecommendations + d.verdictChanges + d.botReplies + d.documentsUpdated + d.implementationsDone + d.idleAlerts;

// ---- Audit trail ---------------------------------------------------------------------------

export const AUDIT_TYPES = ['all', 'decision', 'instruction', 'sync', 'document', 'chat'] as const;
export type AuditType = (typeof AUDIT_TYPES)[number];
export const AUDIT_TYPE_LABEL: Record<AuditType, string> = { all: 'All', decision: 'Decisions', instruction: 'Instructions', sync: 'Syncs', document: 'Downloads', chat: 'Chat posts' };

const ACTION_LABEL: Record<string, string> = {
  decision_yes: 'Approved', decision_no: 'Declined', decision_undo: 'Undid a decision', bulk_decision: 'Bulk decision',
  instruction_instruction: 'Sent an instruction', instruction_question: 'Asked a question', instruction_override: 'Instructed instead', instruction_nudge: 'Resent a nudge',
  sync_request: 'Requested a sync', document_download: 'Downloaded a document', document_zip: 'Downloaded all documents',
  gchat_post_authorized: 'Authorised a Chat post', gchat_posted: 'Chat post sent', gchat_failed: 'Chat post failed', gchat_cancelled: 'Chat post cancelled', gchat_retry: 'Retried a Chat post',
};
export const auditLabel = (action: string): string => ACTION_LABEL[action] || action.replace(/_/g, ' ');

/** The page an audit row's item lives on, or null when it has none (a sync, a bulk batch). */
export function auditLink(r: AuditRow): string | null {
  if (!r.item_id) return null;
  switch (r.item_type) {
    case 'action': return `${BASE}?action=${encodeURIComponent(r.item_id)}`;
    case 'instruction': return `${BASE}?instruction=${encodeURIComponent(r.item_id)}`;
    case 'bot': return bot(r.item_id, '');
    case 'document': return bot(r.item_id.split('/')[0], '?tab=documents');
    default: return null;
  }
}

/** Minutes as "1 h 30 m", or "no data" (never 0) when the stage has nothing to measure. */
export function fmtLatency(minutes: number | null | undefined): string {
  if (minutes == null) return 'no data';
  if (minutes < 1) return '<1 m';
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  return [d ? `${d} d` : '', h ? `${h} h` : '', m && !d ? `${m} m` : ''].filter(Boolean).join(' ') || '0 m';
}
